import { useState, useEffect, useRef } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { collection, addDoc, getDocs, deleteDoc, doc, orderBy, query, Timestamp } from "firebase/firestore";
import { storage, db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";

// ── Types ──────────────────────────────────────────────────────────────
interface BackgroundImage {
  id: string;
  url: string;
  filename: string;
  storagePath: string;
  uploadedAt: Timestamp;
  uploadedBy: string;
  size: number;
}

// ── Helpers ────────────────────────────────────────────────────────────
const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (ts: Timestamp) =>
  ts?.toDate().toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

// ── Component ──────────────────────────────────────────────────────────
const BackgroundGallery: React.FC = () => {
  const { currentUser } = useAuth();
  const [images, setImages] = useState<BackgroundImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<BackgroundImage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Fetch gallery from Firestore ──
  const fetchImages = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "backgroundGallery"),
        orderBy("uploadedAt", "desc")
      );
      const snap = await getDocs(q);
      const items: BackgroundImage[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<BackgroundImage, "id">),
      }));
      setImages(items);
    } catch (err) {
      console.error("Error fetching gallery:", err);
      setError("Failed to load gallery.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // ── Upload handler ──
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !currentUser) return;
    setError(null);

    const validFiles = Array.from(files).filter((f) => {
      if (!f.type.startsWith("image/")) {
        setError("Only image files are allowed.");
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        setError("Each image must be under 10MB.");
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      setUploadProgress(`Uploading ${i + 1} of ${validFiles.length}: ${file.name}`);

      try {
        // Unique filename with timestamp
        const ext = file.name.split(".").pop();
        const filename = `bg_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const storagePath = `assets/backgrounds/${filename}`;

        // Upload to Storage
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        // Save metadata to Firestore
        await addDoc(collection(db, "backgroundGallery"), {
          url,
          filename: file.name,
          storagePath,
          uploadedAt: Timestamp.now(),
          uploadedBy: currentUser.uid,
          size: file.size,
        });
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
        setError(`Failed to upload ${file.name}.`);
      }
    }

    setUploading(false);
    setUploadProgress(null);
    await fetchImages();
  };

  // ── Delete handler ──
  const handleDelete = async (image: BackgroundImage) => {
    if (!window.confirm(`Delete "${image.filename}"? This cannot be undone.`)) return;

    setDeletingId(image.id);
    try {
      // Delete from Storage
      const storageRef = ref(storage, image.storagePath);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, "backgroundGallery", image.id));

      setImages((prev) => prev.filter((img) => img.id !== image.id));
      if (previewImage?.id === image.id) setPreviewImage(null);
    } catch (err) {
      console.error("Delete failed:", err);
      setError("Failed to delete image.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Copy URL ──
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <DashboardWrapper>
      <div className="w-full flex flex-col gap-6 p-4 md:p-6 min-h-screen">

        {/* ── Header ── */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Background Gallery
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {images.length} image{images.length !== 1 ? "s" : ""} available
              for premium users
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Upload Images
          </button>
        </div>

        {/* ── Error ── */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 shrink-0">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        {/* ── Drop Zone ── */}
        <div
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
            ${dragOver ? "border-black bg-gray-50 scale-[1.01]" : "border-gray-200 hover:border-gray-400 hover:bg-gray-50/50"}
            ${uploading ? "pointer-events-none opacity-60" : ""}
          `}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <span className="loading loading-spinner loading-md text-gray-400" />
              <p className="text-sm text-gray-500">{uploadProgress}</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-400">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-700">
                Drop images here or click to browse
              </p>
              <p className="text-xs text-gray-400">
                PNG, JPG, WEBP — up to 10MB each — multiple files supported
              </p>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />

        {/* ── Gallery Grid ── */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <span className="loading loading-spinner loading-lg text-gray-300" />
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8 text-gray-300">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <p className="text-gray-400 text-sm">No background images yet</p>
            <p className="text-gray-300 text-xs">Upload your first image above</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {images.map((image) => (
              <div
                key={image.id}
                className="group relative bg-gray-100 rounded-xl overflow-hidden aspect-video cursor-pointer border border-transparent hover:border-gray-300 transition-all duration-200 hover:shadow-md"
                onClick={() => setPreviewImage(image)}
              >
                {/* Thumbnail */}
                <img
                  src={image.url}
                  alt={image.filename}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                    {/* Preview button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewImage(image); }}
                      className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                      title="Preview"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-gray-700">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(image); }}
                      disabled={deletingId === image.id}
                      className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      {deletingId === image.id ? (
                        <span className="loading loading-spinner loading-xs text-red-500" />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 text-red-500">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6"/><path d="M14 11v6"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Filename label */}
                <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <p className="text-white text-[10px] truncate">{image.filename}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Preview Modal ── */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-2xl animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div className="relative aspect-video bg-gray-100">
              <img
                src={previewImage.url}
                alt={previewImage.filename}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Details */}
            <div className="p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{previewImage.filename}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatBytes(previewImage.size)} · Uploaded {formatDate(previewImage.uploadedAt)}
                  </p>
                </div>
                <button
                  onClick={() => setPreviewImage(null)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-gray-500">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* URL field */}
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-4">
                <p className="text-xs text-gray-500 truncate flex-1 font-mono">{previewImage.url}</p>
                <button
                  onClick={() => handleCopyUrl(previewImage.url)}
                  className="shrink-0 text-xs text-gray-500 hover:text-black font-medium transition-colors"
                >
                  Copy
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleDelete(previewImage)}
                  disabled={deletingId === previewImage.id}
                  className="flex-1 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50 border border-red-200 transition-colors font-medium"
                >
                  {deletingId === previewImage.id ? "Deleting..." : "Delete Image"}
                </button>
                <button
                  onClick={() => setPreviewImage(null)}
                  className="flex-1 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 border border-gray-200 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in {
          animation: modal-in 0.2s ease-out;
        }
      `}</style>
    </DashboardWrapper>
  );
};

export default BackgroundGallery;